import { isLiveRoomId } from "@/lib/api/live-api";
import { isRecitalId } from "@/lib/api/recitals-api";
import { STUDIO_BASE_PATH, isStudioPathname } from "@/lib/studio/studio-routes";

/**
 * Le lien qu'on partage pour faire venir quelqu'un.
 *
 * # Pourquoi un paramètre de requête et pas une route
 *
 * Rejoindre un live, ce n'est pas aller ailleurs : c'est arriver **dans le
 * studio**, déjà connecté. Or le studio est monté en permanence par la coque
 * `(main)` et ses routes ne rendent rien (cf. `studio-routes.ts`) — une route
 * `/live/[room]` afficherait donc une page vide à côté du studio, ou
 * obligerait à un renvoi immédiat vers lui. Le paramètre dit la même chose sans
 * ce détour, et laisse la vue courante — piano-roll, harmonie — intacte.
 *
 * # Pourquoi le front compose et l'API non
 *
 * Seul le navigateur sait sur quelle origine il a été chargé : `localhost`,
 * une IP du réseau local pour l'iPad, `*.orb.local`, la production, ou le
 * schéma interne de la coque Capacitor. Une URL fabriquée côté API serait juste
 * par accident.
 */

export const LIVE_ROOM_PARAM = "live";

/** Lien à transmettre pour rejoindre `room` depuis cette origine. */
export function liveJoinUrl(origin: string, room: string): string {
  return `${origin.replace(/\/$/, "")}${STUDIO_BASE_PATH}?${LIVE_ROOM_PARAM}=${room}`;
}

/**
 * L'adresse du studio une fois dans `room`, **sur la vue où l'on est** : ouvrir
 * une séance ou en rejoindre une par son code ne fait pas quitter l'harmonie
 * pour le rouleau. Hors du studio, la vue par défaut.
 *
 * Relative, pour `router.replace` ; le lien à partager, lui, est
 * `liveJoinUrl` — la vue de l'un n'a pas à devenir celle de l'autre.
 */
export function liveStudioPath(pathname: string, room: string): string {
  const base = isStudioPathname(pathname) ? pathname : STUDIO_BASE_PATH;

  return `${base}?${LIVE_ROOM_PARAM}=${room}`;
}

/**
 * Salle demandée par une URL, `null` quand il n'y en a pas ou qu'elle est
 * malformée. Une valeur trafiquée est traitée comme une absence : le studio
 * s'ouvre normalement plutôt que d'afficher une erreur pour un paramètre que
 * personne n'a tapé volontairement.
 */
export function liveRoomFromParams(params: URLSearchParams | null): string | null {
  const raw = params?.get(LIVE_ROOM_PARAM);

  if (raw === null || raw === undefined || !isLiveRoomId(raw)) {
    return null;
  }

  return raw;
}

/** Le paramètre qui dit au studio dans quel récital il est. */
export const LIVE_RECITAL_PARAM = "recital";

/**
 * L'adresse du studio pendant un récital.
 *
 * Elle porte le récital et non la salle : copiée et partagée, elle ne fait
 * entrer personne directement, elle renvoie à la salle d'attente (voir
 * `lib/live/recital-entry.ts`). C'est ce qui permet à l'interprète de partager
 * sa barre d'adresse.
 */
export function recitalStudioUrl(origin: string, recitalId: string): string {
  return `${origin.replace(/\/$/, "")}${STUDIO_BASE_PATH}?${LIVE_RECITAL_PARAM}=${encodeURIComponent(recitalId)}`;
}

/** Le paramètre qui dit au studio quel récital il rejoue. */
export const LIVE_REPLAY_PARAM = "replay";

/**
 * L'adresse du studio pour rejouer un récital enregistré.
 *
 * Le même studio que le direct, la même adresse à un paramètre près : ce qui
 * change est la source des notes, pas l'écran qui les montre.
 */
export function recitalReplayPath(recitalId: string): string {
  return `${STUDIO_BASE_PATH}?${LIVE_REPLAY_PARAM}=${encodeURIComponent(recitalId)}`;
}

/**
 * L'adresse du studio sur une prise publiée : `/studio?take=<uuid>`.
 *
 * Sans le récital : ni son identifiant ni le rang de la prise ne sortent dans
 * un lien partagé — le studio retrouve le récital par l'API (voir
 * `ReplayProvider`).
 */
export function takeReplayPath(takeId: string): string {
  return `${STUDIO_BASE_PATH}?take=${encodeURIComponent(takeId)}`;
}

export function recitalIdFromParams(params: URLSearchParams | null): string | null {
  const raw = params?.get(LIVE_RECITAL_PARAM);

  if (raw === null || raw === undefined || !isRecitalId(raw)) {
    return null;
  }

  return raw;
}
